import { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { ChevronLeft, ExternalLink, BookOpen } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getLiturgicalDay } from '@/lib/liturgical/calendar';
import { getColorHex, getColorName, getSeasonName, getRankName } from '@/lib/liturgical/colors';
import { useUSCCBReadings } from '@/hooks/useUSCCBReadings';
import { ReadingBlock } from '@/components/ReadingBlock';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

export default function MassPage() {
  const { dateStr } = useParams<{ dateStr: string }>();
  const date = useMemo(() => {
    if (!dateStr) return null;
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return null;
    const [y, m, d] = parts;
    return new Date(y, m - 1, d);
  }, [dateStr]);

  if (!date || isNaN(date.getTime())) return <Navigate to="/" replace />;
  return <MassContent date={date} />;
}

function MassContent({ date }: { date: Date }) {
  const day = useMemo(() => getLiturgicalDay(date), [date]);
  const colorHex = getColorHex(day.color);
  const { data: readings, isLoading } = useUSCCBReadings(date);
  const label = `${WEEKDAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

  useSeoMeta({
    title: `${day.name} — Nostr Missal`,
    description: `Mass for ${label}. ${getSeasonName(day.season)}, ${getColorName(day.color)}.`,
  });

  const url = (() => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(2);
    return `https://bible.usccb.org/bible/readings/${mm}${dd}${yy}.cfm`;
  })();

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <Link to="/calendar">
          <Button variant="ghost" size="sm" className="mb-6 gap-1.5 text-stone-500 hover:text-stone-900 dark:hover:text-stone-100">
            <ChevronLeft className="h-4 w-4" /> Calendar
          </Button>
        </Link>

        {/* Hero — clean white, thin color bar only */}
        <div className="overflow-hidden rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 mb-8">
          <div className="h-1 w-full" style={{ backgroundColor: colorHex }} />
          <div className="px-6 pt-6 pb-7 text-center sm:px-10">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-400 dark:text-stone-500">{label}</p>
            <h1 className="mx-auto max-w-lg text-2xl font-semibold leading-snug sm:text-3xl text-stone-900 dark:text-stone-100" style={{ fontFamily: '"Cinzel", serif' }}>
              {day.name}
            </h1>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 dark:border-stone-700 px-3 py-1 text-[11px] font-semibold text-stone-500 dark:text-stone-400">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colorHex }} />
                {getColorName(day.color)}
              </span>
              <span className="rounded-full border border-stone-200 dark:border-stone-700 px-3 py-1 text-[11px] font-medium text-stone-500 dark:text-stone-400">
                {getSeasonName(day.season)}
              </span>
              {day.rank !== 'weekday' && (
                <span className="rounded-full border border-stone-200 dark:border-stone-700 px-3 py-1 text-[11px] font-medium text-stone-500 dark:text-stone-400">
                  {getRankName(day.rank)}
                </span>
              )}
            </div>
            {day.saintInfo && <p className="mt-3 text-sm italic text-stone-400 dark:text-stone-500">{day.saintInfo}</p>}
          </div>
        </div>

        {/* Readings */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-stone-800 dark:text-stone-200" style={{ fontFamily: '"Cinzel", serif' }}>Readings</h2>
          <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12px] text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors">
            USCCB.org <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 overflow-hidden">
                <div className="flex">
                  <div className="w-1 shrink-0 bg-stone-200 dark:bg-stone-700" />
                  <div className="flex-1 p-5 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-3/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {readings && readings.readings.length > 0 && (
          <div className="space-y-4">
            {readings.readings.map((r, i) => (
              <ReadingBlock key={i} reading={r} />
            ))}
          </div>
        )}

        {!isLoading && (!readings || readings.readings.length === 0) && (
          <div className="rounded-xl border border-dashed border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 py-14 text-center px-6">
            <BookOpen className="h-8 w-8 text-stone-300 dark:text-stone-600 mx-auto mb-4" />
            <p className="max-w-xs mx-auto text-sm text-stone-500 dark:text-stone-400 leading-relaxed">
              Readings not available. View on USCCB.org.
            </p>
            <a href={url} target="_blank" rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-200 underline underline-offset-4 transition-colors">
              Visit USCCB.org <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        )}

        <div className="mt-12 border-t border-stone-200 dark:border-stone-700 pt-8 text-center">
          <p className="text-stone-300 dark:text-stone-600 text-lg tracking-[0.5em] mb-4 select-none">✦</p>
          <Link to="/ordo" className="inline-flex items-center gap-2 rounded-full border border-stone-300 dark:border-stone-600 px-6 py-2.5 text-sm font-semibold text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition-all"
            style={{ fontFamily: '"Cinzel", serif', letterSpacing: '0.06em' }}>
            <BookOpen className="h-4 w-4" /> Order of Mass
          </Link>
        </div>
      </div>
    </Layout>
  );
}
